import React, { useCallback, useEffect, useState } from "react";
import Web3Service, {
  AutomationWalletStatus,
  AUTOMATION_LOW_BNB,
  EXPLORER_URL,
  NETWORK_LABEL,
  WrongNetworkError,
  truncateDecimals,
  hasPositiveBalance,
  subtractDecimals,
  compareDecimals,
} from "../services/Web3Services";

const MUTED = "#8A9992";
const DIM = "#7A8880";
const SOFT = "#D6E2DC";
const MINT = "#5EE9B5";
const AMBER = "#F5B44C";
const RED = "#F0616B";
const INK = "#F2F7F4";

const INPUT: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  background: "rgba(0,0,0,0.4)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 8,
  padding: "9px 44px 9px 11px",
  color: INK,
  fontSize: 13,
  fontFamily: "'JetBrains Mono',monospace",
  outline: "none",
};

const chip = (tone = MINT, disabled = false): React.CSSProperties => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  border: `1px solid ${tone}40`,
  background: `${tone}14`,
  color: tone,
  borderRadius: 999,
  padding: "4px 10px",
  cursor: disabled ? "not-allowed" : "pointer",
  fontFamily: "'Sora',sans-serif",
  fontSize: 11,
  fontWeight: 600,
  opacity: disabled ? 0.5 : 1,
  whiteSpace: "nowrap",
  textDecoration: "none",
});

const primaryBtn = (disabled: boolean): React.CSSProperties => ({
  width: "100%",
  border: 0,
  borderRadius: 9,
  padding: 11,
  fontSize: 14,
  fontWeight: 600,
  fontFamily: "'Sora',sans-serif",
  cursor: disabled ? "not-allowed" : "pointer",
  background: "linear-gradient(180deg, #19B37C, #0E8A5C)",
  color: "#03150D",
  opacity: disabled ? 0.5 : 1,
});

const PRESETS = ["0.05", "0.1", "0.25"];

/**
 * Automation wallet — centred modal opened from the admin status strip.
 *
 * The off-chain bot signs `performUpkeep` with this wallet and pays its own
 * BNB gas, so an empty wallet silently stops automation. This panel surfaces
 * the balance and lets an admin top it up from the connected wallet.
 */
const AutomationWalletPanel: React.FC<{ open: boolean; onClose: () => void }> = ({
  open,
  onClose,
}) => {
  const [status, setStatus] = useState<AutomationWalletStatus | null>(null);
  const [myBalance, setMyBalance] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [amount, setAmount] = useState("");
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [s, mine] = await Promise.all([
        Web3Service.getAutomationWalletStatus(),
        Web3Service.getBnbBalance().catch(() => null),
      ]);
      setStatus(s);
      setMyBalance(mine);
    } catch (error: any) {
      if (error instanceof WrongNetworkError) {
        setLoadError(`Switch your wallet to ${NETWORK_LABEL}.`);
      } else {
        console.error("[AutomationWalletPanel] load failed", error);
        setLoadError("Couldn't read the automation wallet balance.");
      }
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [open, onClose]);

  if (!open) return null;

  // Leave gas behind — sending the entire balance would leave nothing to pay
  // for the transfer itself.
  const GAS_RESERVE = 0.002;
  const useMax = () => {
    if (!myBalance) return;
    const spendable = Number(myBalance) - GAS_RESERVE;
    if (spendable <= 0) {
      setMessage({ ok: false, text: "Not enough BNB to cover a transfer plus gas." });
      return;
    }
    setAmount((Math.floor(spendable * 1e6) / 1e6).toString());
  };

  const send = async () => {
    if (!hasPositiveBalance(amount)) {
      setMessage({ ok: false, text: "Enter an amount greater than zero." });
      return;
    }
    setSending(true);
    setMessage(null);
    try {
      const { txHash } = await Web3Service.fundAutomationWallet(amount);
      setMessage({ ok: true, text: `Sent ${amount} BNB. Tx ${txHash.slice(0, 12)}…` });
      setAmount("");
      await refresh();
    } catch (error: any) {
      console.error("[AutomationWalletPanel] transfer failed", error);
      setMessage({ ok: false, text: error?.message?.slice(0, 160) || "Transfer failed." });
    } finally {
      setSending(false);
    }
  };

  const low = status?.isLow ?? false;
  const shortfall = status ? subtractDecimals(AUTOMATION_LOW_BNB, status.balance) : "0";

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        background: "rgba(0,2,5,0.72)",
        backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        fontFamily: "'Sora',sans-serif",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="cvt-scroll"
        style={{
          width: "min(480px,100%)",
          maxHeight: "86vh",
          overflowY: "auto",
          background: "#060B08",
          border: `1px solid ${low ? "rgba(240,97,107,0.28)" : "rgba(255,255,255,0.10)"}`,
          borderRadius: 16,
          padding: 22,
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: INK }}>
              Automation wallet
            </h2>
            <p style={{ margin: "5px 0 0", fontSize: 12, color: MUTED, lineHeight: 1.5 }}>
              The bot signs <code style={{ fontSize: 11 }}>performUpkeep</code> with this
              wallet and pays its own gas. If it runs dry, claims stop processing.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, flex: "none" }}>
            <button onClick={refresh} disabled={loading} style={chip(MINT, loading)}>
              {loading ? "…" : "Refresh"}
            </button>
            <button onClick={onClose} style={chip(MINT)}>Close</button>
          </div>
        </div>

        {loadError && (
          <div style={{ borderRadius: 8, border: "1px solid rgba(240,97,107,0.3)", background: "rgba(240,97,107,0.1)", padding: "8px 10px", fontSize: 12, color: "#FF9AA2" }}>
            {loadError}
          </div>
        )}

        {/* status */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            borderRadius: 12,
            border: `1px solid ${low ? "rgba(240,97,107,0.32)" : "rgba(52,211,153,0.30)"}`,
            background: low ? "rgba(240,97,107,0.10)" : "rgba(52,211,153,0.08)",
            padding: "12px 14px",
          }}
        >
          <span
            style={{ width: 8, height: 8, borderRadius: 999, flex: "none", background: low ? RED : MINT }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: low ? "#FF9AA2" : MINT }}>
              {!status ? "Loading…" : low ? "Needs a top-up" : "Funded"}
            </div>
            {status && (
              <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>
                {low
                  ? `${truncateDecimals(shortfall, 4)} BNB below the ${AUTOMATION_LOW_BNB} BNB threshold`
                  : `Above the ${AUTOMATION_LOW_BNB} BNB threshold`}
              </div>
            )}
          </div>
          <div
            style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 16, color: INK, flex: "none" }}
            title={status ? `${status.balance} BNB` : undefined}
          >
            {status ? `${truncateDecimals(status.balance, 4)}` : "—"}
            <span style={{ fontSize: 11, color: MUTED }}> BNB</span>
          </div>
        </div>

        {/* address */}
        {status && (
          <div>
            <div style={{ fontSize: 11, color: DIM, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600, marginBottom: 5 }}>
              Wallet address
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(status.address);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1600);
                }}
                title="Click to copy"
                style={{
                  flex: 1,
                  minWidth: 0,
                  textAlign: "left",
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.09)",
                  borderRadius: 999,
                  padding: "7px 14px",
                  cursor: "pointer",
                  fontFamily: "'JetBrains Mono',monospace",
                  fontSize: 12,
                  color: SOFT,
                  overflowWrap: "anywhere",
                }}
              >
                {status.address}
              </button>
              <span style={{ fontSize: 11, color: copied ? MINT : MUTED, flex: "none" }}>
                {copied ? "Copied" : ""}
              </span>
            </div>
            <a
              href={`${EXPLORER_URL}/address/${status.address}`}
              target="_blank"
              rel="noreferrer"
              style={{ ...chip(MINT), marginTop: 8 }}
            >
              View on explorer ↗
            </a>
          </div>
        )}

        {/* top up */}
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 14, display: "flex", flexDirection: "column", gap: 9 }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
            <div style={{ fontSize: 11, color: DIM, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>
              Send BNB
            </div>
            {myBalance !== null && (
              <span style={{ fontSize: 11, color: MUTED }} title={`${myBalance} BNB`}>
                your wallet: {truncateDecimals(myBalance, 4)} BNB
              </span>
            )}
          </div>

          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {PRESETS.map((v) => (
              <button
                key={v}
                onClick={() => setAmount(v)}
                style={{
                  padding: "6px 12px",
                  borderRadius: 999,
                  border: `1px solid ${amount === v ? `${MINT}66` : "rgba(255,255,255,0.12)"}`,
                  background: amount === v ? `${MINT}18` : "rgba(0,0,0,0.3)",
                  color: amount === v ? MINT : MUTED,
                  cursor: "pointer",
                  fontFamily: "'Sora',sans-serif",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {v}
              </button>
            ))}
          </div>

          <div style={{ position: "relative" }}>
            <input
              style={INPUT}
              inputMode="decimal"
              placeholder="0.0"
              value={amount}
              disabled={sending}
              onChange={(e) => setAmount(e.target.value)}
            />
            <button
              onClick={useMax}
              disabled={sending || myBalance === null}
              style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                background: "transparent",
                border: 0,
                color: MINT,
                cursor: "pointer",
                fontFamily: "'Sora',sans-serif",
                fontSize: 11,
                fontWeight: 600,
                opacity: sending || myBalance === null ? 0.5 : 1,
              }}
            >
              MAX
            </button>
          </div>

          <button
            onClick={send}
            disabled={sending || !hasPositiveBalance(amount)}
            style={primaryBtn(sending || !hasPositiveBalance(amount))}
          >
            {sending ? "Sending…" : "Send BNB"}
          </button>

          {/* Warn before a transfer that would leave the sender unable to pay gas */}
          {myBalance !== null &&
            hasPositiveBalance(amount) &&
            compareDecimals(amount, myBalance) >= 0 && (
              <div style={{ fontSize: 11, color: AMBER }}>
                That is your whole balance — leave some BNB for gas.
              </div>
            )}

          <div style={{ fontSize: 11, color: DIM, lineHeight: 1.5 }}>
            Sends from your connected wallet directly to the bot. A plain BNB
            transfer, no contract call.
          </div>
        </div>

        {message && (
          <div
            style={{
              fontSize: 12,
              lineHeight: 1.5,
              borderRadius: 8,
              padding: "8px 10px",
              color: message.ok ? MINT : "#FF9AA2",
              background: message.ok ? "rgba(94,233,181,0.08)" : "rgba(240,97,107,0.08)",
              border: `1px solid ${message.ok ? "rgba(94,233,181,0.25)" : "rgba(240,97,107,0.25)"}`,
            }}
          >
            {message.text}
          </div>
        )}
      </div>
    </div>
  );
};

export default AutomationWalletPanel;
